// ─────────────────────────────────────────────────────────────────────────────
// dp-variant-datepicker.js
// Kalender-Auswahl für Termin-Varianten. Steuert den (ausgeblendeten)
// Impact-Variantenpicker, damit Preis, Buy-Button, URL & Sticky-Bar
// wie gewohnt über das Theme aktualisiert werden.
// ─────────────────────────────────────────────────────────────────────────────

const TZ = 'Europe/Berlin';
const LOW_STOCK_THRESHOLD = 6; // < 6 Tickets → oranger Balken

const MONTHS = ['Jan', 'Feb', 'März', 'Apr', 'Mai', 'Juni', 'Juli', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];

const _keyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
});
const _timeFmt = new Intl.DateTimeFormat('de-DE', {
  timeZone: TZ, hour: '2-digit', minute: '2-digit'
});
const _longFmt = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
});

const pad = (n) => String(n).padStart(2, '0');

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

// Tages-Keys (YYYY-MM-DD) werden als UTC-Datum gerechnet → keine DST-Probleme
function toKey(y, m, d) {
  const dt = new Date(Date.UTC(y, m, d));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

function keyToUTCDate(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatShort(key) {
  const dt = keyToUTCDate(key);
  return `${WEEKDAYS[dt.getUTCDay()]} ${pad(dt.getUTCDate())}.${pad(dt.getUTCMonth() + 1)}.${dt.getUTCFullYear()}`;
}

function formatLong(key) {
  return _longFmt.format(keyToUTCDate(key));
}

// Datum: Metafeld-Timestamp, Fallback auf "14.10.2026" im Variantentitel
function dateKeyOf(v) {
  if (v.ts) return _keyFmt.format(new Date(v.ts * 1000));
  const m = (v.title || '').match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  return m ? toKey(+m[3], +m[2] - 1, +m[1]) : null;
}

// Uhrzeit: aus dem Titel ("18:00 Uhr"), Fallback auf den Timestamp
function timeOf(v) {
  const m = (v.title || '').match(/(\d{1,2}):(\d{2})/);
  if (m) return `${pad(m[1])}:${m[2]} Uhr`;
  if (v.ts) {
    const t = _timeFmt.format(new Date(v.ts * 1000));
    if (t !== '00:00') return `${t} Uhr`;
  }
  return '';
}


class DpVariantDatepicker extends HTMLElement {
  connectedCallback() {
    if (!this._setupDone && !this._setup()) return;
    this._bind();
    this.setAttribute('ready', '');
  }

  disconnectedCallback() {
    this._unbind();
  }

  // ─── Einmaliges Setup ────────────────────────────────────────────────────
  _setup() {
    const dataEl = this.querySelector('.dpvd__data');
    if (!dataEl) return false;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[dp] Datepicker-Daten fehlerhaft', e);
      return false;
    }

    this.today = _keyFmt.format(new Date());
    this.byDay = new Map();

    this.variants.forEach((v) => {
      v.key = dateKeyOf(v);
      v.time = timeOf(v);
      if (!v.key) return;
      if (!this.byDay.has(v.key)) this.byDay.set(v.key, []);
      this.byDay.get(v.key).push(v);
    });
    this.byDay.forEach((list) => {
      list.sort((a, b) => (a.ts || 0) - (b.ts || 0) || a.time.localeCompare(b.time));
    });

    const keys = [...this.byDay.keys()].sort();
    const upcoming = keys.filter((k) => k >= this.today);
    this.minMonth = (upcoming[0] || keys[0] || this.today).slice(0, 7);
    this.maxMonth = (keys[keys.length - 1] || this.today).slice(0, 7);

    this.selectedId = Number(this.dataset.selectedId) || null;
    this.pendingKey = null;
    this.isOpen = false;

    this.trigger = this.querySelector('.dpvd__trigger');
    this.valueEl = this.querySelector('.dpvd__value');
    this.popoverEl = this.querySelector('.dpvd__popover');
    this.monthEl = this.querySelector('.dpvd__month');
    this.prevBtn = this.querySelector('.dpvd__nav-btn[data-dir="-1"]');
    this.nextBtn = this.querySelector('.dpvd__nav-btn[data-dir="1"]');
    this.weeksEl = this.querySelector('.dpvd__weeks');
    this.timesEl = this.querySelector('.dpvd__times');

    const sel = this._selected();
    this._setView((sel && sel.key) || upcoming[0] || keys[0] || this.today);

    // Listener auf eigenen Kindelementen (bleiben bei Re-Attach bestehen)
    this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.prevBtn.addEventListener('click', () => this._shiftMonth(-1));
    this.nextBtn.addEventListener('click', () => this._shiftMonth(1));

    this.weeksEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.dpvd__day');
      if (btn && !btn.disabled) this._onDay(btn.dataset.key);
    });

    this.timesEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.dpvd__time');
      if (!btn || btn.disabled) return;
      const v = this._byId(Number(btn.dataset.id));
      if (v) this._select(v);
    });

    // Globale Listener (werden in _bind/_unbind verwaltet)
    this._onDocClick = (e) => {
      // composedPath statt contains(): geklickte Buttons werden beim
      // Re-Render ersetzt und wären sonst schon "außerhalb"
      if (this.isOpen && !e.composedPath().includes(this)) this.close();
    };
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    };
    this._onVariantChange = (e) => {
      const id = e.detail?.variant?.id;
      if (!id || id === this.selectedId || !this._byId(id)) return;
      this.selectedId = id;
      this._updateTrigger();
      if (this.isOpen) this._render();
    };

    this._updateTrigger();
    this._setupDone = true;
    return true;
  }

  _bind() {
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('variant:change', this._onVariantChange);
  }

  _unbind() {
    if (!this._setupDone) return;
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  // ─── Helfer ──────────────────────────────────────────────────────────────
  _byId(id) {
    return this.variants.find((v) => v.id === id) || null;
  }

  _selected() {
    return this._byId(this.selectedId);
  }

  _isBookable(v) {
    return v.available && v.key >= this.today;
  }

  _setView(key) {
    this.viewY = Number(key.slice(0, 4));
    this.viewM = Number(key.slice(5, 7)) - 1;
  }

  _viewMonth() {
    return `${this.viewY}-${pad(this.viewM + 1)}`;
  }

  _shiftMonth(dir) {
    this._setView(toKey(this.viewY, this.viewM + dir, 1));
    this._render();
  }

  // ─── Öffnen / Schließen ──────────────────────────────────────────────────
  open() {
    const sel = this._selected();
    if (sel && sel.key) this._setView(sel.key);
    this.pendingKey = sel && sel.key && (this.byDay.get(sel.key) || []).length > 1 ? sel.key : null;

    this.isOpen = true;
    this._render();
    this.popoverEl.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.pendingKey = null;
    this.popoverEl.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  // ─── Rendering ───────────────────────────────────────────────────────────
  _render() {
    this.monthEl.textContent = `${MONTHS[this.viewM]} ${this.viewY}`;
    this.prevBtn.disabled = this._viewMonth() <= this.minMonth;
    this.nextBtn.disabled = this._viewMonth() >= this.maxMonth;
    this._renderWeeks();
    this._renderTimes();
  }

  _renderWeeks() {
    const sel = this._selected();
    const month = this._viewMonth();
    const offset = (new Date(Date.UTC(this.viewY, this.viewM, 1)).getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(this.viewY, this.viewM + 1, 0)).getUTCDate();
    const cells = Math.ceil((offset + daysInMonth) / 7) * 7;

    let html = '';
    for (let i = 0; i < cells; i++) {
      if (i % 7 === 0) html += '<div class="dpvd__week">';

      const key = toKey(this.viewY, this.viewM, 1 - offset + i);
      const list = this.byDay.get(key) || [];
      const bookable = list.filter((v) => this._isBookable(v));
      const isFuture = key >= this.today;
      const isSelected = sel && sel.key === key;
      const isLow = bookable.length > 0 &&
        bookable.every((v) => v.qty !== null && v.qty < LOW_STOCK_THRESHOLD);

      const cls = ['dpvd__day'];
      if (key.slice(0, 7) !== month) cls.push('dpvd__day--outside');
      if (key === this.today) cls.push('dpvd__day--today');
      if (list.length && isFuture) cls.push(bookable.length ? 'dpvd__day--event' : 'dpvd__day--soldout');
      if (isLow) cls.push('dpvd__day--low');
      if (isSelected) cls.push('dpvd__day--selected');
      if (this.pendingKey === key && !isSelected) cls.push('dpvd__day--pending');

      let label = formatLong(key);
      if (!list.length) label += ', kein Termin';
      else if (!isFuture) label += ', vergangen';
      else if (!bookable.length) label += ', ausverkauft';
      else if (isLow) label += ', nur noch wenige Tickets';

      html += `<button type="button" class="${cls.join(' ')}" data-key="${key}"`
        + ` aria-label="${esc(label)}"`
        + (isSelected ? ' aria-pressed="true"' : '')
        + (bookable.length ? '' : ' disabled')
        + `>${Number(key.slice(8))}`
        + (list.length && isFuture ? '<span class="dpvd__bar" aria-hidden="true"></span>' : '')
        + '</button>';

      if (i % 7 === 6) html += '</div>';
    }
    this.weeksEl.innerHTML = html;
  }

  _renderTimes() {
    const key = this.pendingKey;
    const list = key ? this.byDay.get(key) || [] : [];

    if (list.length < 2) {
      this.timesEl.hidden = true;
      this.timesEl.innerHTML = '';
      return;
    }

    const sel = this._selected();
    const buttons = list.map((v) => {
      const ok = this._isBookable(v);
      return `<button type="button" class="dpvd__time" data-id="${v.id}"`
        + ` aria-pressed="${sel && sel.id === v.id ? 'true' : 'false'}"`
        + (ok ? '' : ' disabled')
        + `>${esc(v.time || v.title)}${ok ? '' : ' · ausverkauft'}</button>`;
    }).join('');

    this.timesEl.innerHTML =
      `<p class="dpvd__times-title">Uhrzeit am ${formatShort(key)}</p>`
      + `<div class="dpvd__times-list">${buttons}</div>`;
    this.timesEl.hidden = false;
  }

  _updateTrigger() {
    const sel = this._selected();
    if (!sel) return;
    let text = sel.key ? formatShort(sel.key) : sel.title;
    if (sel.key && sel.time && (this.byDay.get(sel.key) || []).length > 1) {
      text += ` · ${sel.time}`;
    }
    this.valueEl.textContent = text;
  }

  // ─── Auswahl ─────────────────────────────────────────────────────────────
  _onDay(key) {
    const list = this.byDay.get(key) || [];
    const bookable = list.filter((v) => this._isBookable(v));
    if (!bookable.length) return;

    // Ein Termin am Tag → direkt wählen, mehrere → Uhrzeiten anzeigen
    if (list.length === 1) {
      this._select(bookable[0]);
      return;
    }
    this.pendingKey = key;
    this._render();
  }

  _select(variant) {
    this.selectedId = variant.id;
    this._updateTrigger();
    this.close();
    this._applyToForm(variant);
  }

  // Wählt die Variante im (ausgeblendeten) Impact-Picker aus, damit das
  // Theme Preis, Buy-Button, URL usw. selbst aktualisiert.
  _applyToForm(variant) {
    const scope = this.closest('product-rerender') || document;
    const picker = scope.querySelector('variant-picker');
    if (picker && this._selectOptions(picker, variant)) return;

    // Fallback: Varianten-ID direkt ins Formular schreiben
    const formId = this.closest('product-rerender')?.getAttribute('observe-form');
    const form = formId
      ? document.getElementById(formId)
      : scope.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('[name="id"]');
    if (idInput && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _selectOptions(picker, variant) {
    // Inputs nach Optionsgruppe (name) in Reihenfolge gruppieren
    const groups = [];
    picker.querySelectorAll('input[type="radio"][name], select[name]').forEach((el) => {
      let g = groups.find((x) => x.name === el.name);
      if (!g) groups.push((g = { name: el.name, els: [] }));
      g.els.push(el);
    });
    if (!groups.length) return false;

    const matches = (el, value) =>
      el.value === value ||
      el.dataset.value === value ||
      el.labels?.[0]?.textContent.trim() === value;

    let changed = null;
    for (let i = 0; i < variant.options.length; i++) {
      const group = groups[i];
      const value = variant.options[i];
      if (!group) return false;

      const first = group.els[0];
      if (first.tagName === 'SELECT') {
        const opt = [...first.options].find((o) => o.value === value || o.textContent.trim() === value);
        if (!opt) return false;
        if (!opt.selected) {
          first.value = opt.value;
          changed = first;
        }
      } else {
        const radio = group.els.find((r) => matches(r, value));
        if (!radio) return false;
        if (!radio.checked) {
          radio.checked = true;
          changed = radio;
        }
      }
    }

    // Nur ein change-Event → nur ein Re-Render durch das Theme
    if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
}

if (!customElements.get('dp-variant-datepicker')) {
  customElements.define('dp-variant-datepicker', DpVariantDatepicker);
}